import { useEffect, useState } from 'react';
import { IntersectionOptions, useInView } from 'react-intersection-observer';

/**
 * delay doesn't work in all browsers, and this hook is a workaround for that,
 * as well as uses a trigger only once strategy.
 */
export type TriggerOnceIntersectionOptions = Omit<IntersectionOptions, 'delay' | 'triggerOnce'> & {
  /**
   * The number of milliseconds to delay the trigger of the inView state.
   *
   * inView will only get set to true if the element is still in view after the delay.
   */
  delayMs: number;
};

/**
 * This is a trigger only once implementation of the react-intersection-observer,
 * with a working delay across all browsers.
 */
export const useInViewTriggerOnce = (
  intersectionOptions: TriggerOnceIntersectionOptions,
): [(node?: Element | null) => void, boolean] => {
  const { delayMs } = intersectionOptions;
  const [resultInView, setResultInView] = useState(false);
  const [ref, inView] = useInView({
    ...intersectionOptions,
    // turn it off after in view the first time, or if configured as skip
    skip: resultInView || intersectionOptions.skip,
  });

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;

    if (inView) {
      timerId = setTimeout(() => setResultInView(true), delayMs);
    }

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [inView, delayMs]);

  return [ref, resultInView];
};
